import * as React from 'react'

import UnitTemplateFilterInputs from './unitTemplateFilterInputs'
import UnitTemplateRow from './unitTemplateRow'

import { flagOptions } from '../../../../constants/flagOptions'
import { requestGet, } from '../../../../modules/request/index'
import { SortableList, Tooltip, Spinner } from '../../../Shared/index';
import getAuthToken from '../../components/modules/get_auth_token'
import { ALL_DIAGNOSTICS, ALL_FLAGS, ALL_PACKS, changedOrderNumbers, moveUnitTemplate, orderedUnitTemplates } from '../../helpers/unitTemplates'

const UNIT_TEMPLATES_URL = `${window.postbuildEnv.DEFAULT_URL}/cms/unit_templates.json`
const DIAGNOSTICS_URL = `${window.postbuildEnv.DEFAULT_URL}/api/v1/activities/diagnostic_activities.json`
const UPDATE_ORDER_URL = `${window.postbuildEnv.DEFAULT_URL}/cms/unit_templates/update_order_numbers`

// TODO: flag display
const flagOptionValues = flagOptions.map(option => option.value)
const headerHash = {
  'Accept': 'application/json',
  'Content-Type': 'application/json',
  'X-CSRF-Token': getAuthToken()
}
const ERROR_MESSAGE = 'Failed to fetch activities-- please refresh the page.'
const ORDER_ERROR_MESSAGE = 'Failed to save the new activity pack order-- please refresh the page and try again.'
const NO_RESULTS_MESSAGE = 'No activity packs match these filters.'

function renderDragPreview(row) {
  return <div className="unit-template-drag-preview">{row.props.unitTemplate.name}</div>
}

export const UnitTemplates = () => {

  const [loadingTableData, setLoadingTableData] = React.useState<boolean>(true);
  const [flag, setFlag] = React.useState<string>(ALL_FLAGS)
  const [fetchedData, setFetchedData] = React.useState<any>([])
  const [searchInput, setSearchInput] = React.useState<string>("")
  const [diagnostics, setDiagnostics] = React.useState<any[]>([])
  const [diagnostic, setDiagnostic] = React.useState<string>(ALL_DIAGNOSTICS)
  const [packType, setPackType] = React.useState<string>(ALL_PACKS)
  const [error, setError] = React.useState<string>(null);
  const [searchByActivityPack, setSearchByActivityPack] = React.useState<boolean>(true);

  React.useEffect(() => {
    if (loadingTableData && !diagnostics.length) {
      fetchDiagnosticsData()
    }
  }, [diagnostics])

  React.useEffect(() => {
    if (loadingTableData && !fetchedData.length) {
      fetchUnitTemplatesData()
    }
  }, [fetchedData])

  React.useEffect(() => {
    if (loadingTableData && fetchedData.length && diagnostic.length) {
      setLoadingTableData(false)
    }
  }, [fetchedData, diagnostics])

  function fetchUnitTemplatesData() {
    setLoadingTableData(true)
    requestGet(
      UNIT_TEMPLATES_URL,
      (body) => {
        setError(null);
        setFetchedData(body.unit_templates)
      },
      (body) => {
        setLoadingTableData(false);
        setError(ERROR_MESSAGE)
      }
    )
  }

  function fetchDiagnosticsData() {
    requestGet(
      DIAGNOSTICS_URL,
      (body) => {
        setError(null);
        setDiagnostics(body.diagnostics)
      },
      (body) => {
        setLoadingTableData(false);
        setError(ERROR_MESSAGE)
      }
    )
  }

  function updateOrder(_reorderedRows, { movedId, targetId, position }) {
    const reorderedUnitTemplates = moveUnitTemplate({ unitTemplates: fetchedData, movedId, targetId, position })
    const changedUnitTemplates = changedOrderNumbers({ previous: fetchedData, updated: reorderedUnitTemplates })
    setFetchedData(reorderedUnitTemplates)
    if (!changedUnitTemplates.length) { return }

    fetch(UPDATE_ORDER_URL, {
      method: 'PUT',
      mode: 'cors',
      credentials: 'include',
      body: JSON.stringify({unit_templates: changedUnitTemplates}),
      headers: headerHash,
    }).then((response) => {
      if (!response.ok) {
        throw Error(response.statusText);
      }
    }).catch(() => {
      alert(ORDER_ERROR_MESSAGE)
    })
  };

  function onDelete(id) {
    const link = `${window.postbuildEnv.DEFAULT_URL}/cms/unit_templates/${id}`
    fetch(link, {
      method: 'DELETE',
      mode: 'cors',
      credentials: 'include',
      headers: {
        'X-CSRF-Token': getAuthToken()
      }
    }).then((response) => {
      if (!response.ok) {
        throw Error(response.statusText);
      }
      return response.json();
    }).then((response) => {
      alert(`Activity pack was deleted.`)
      const updatedActivities = [...fetchedData].filter(activity => activity.id !== id);
      setFetchedData(updatedActivities);
    }).catch((error) => {
      // to do, use Sentry to capture error
    })
  }

  function updateUnitTemplate(unitTemplate) {
    const newUnitTemplate = unitTemplate
    newUnitTemplate.unit_template_category_id = unitTemplate.unit_template_category.id
    newUnitTemplate.activity_ids = unitTemplate.activity_ids || unitTemplate.activities.map((a) => a.id)
    const link = `${window.postbuildEnv.DEFAULT_URL}/cms/unit_templates/${unitTemplate.id}.json`
    const index = fetchedData.findIndex((e) => e.id === unitTemplate.id)
    fetch(link, {
      method: 'PUT',
      mode: 'cors',
      credentials: 'include',
      body: JSON.stringify({unit_template: newUnitTemplate}),
      headers: headerHash,
    }).then((response) => {
      if (!response.ok) {
        throw Error(response.statusText);
      }
      return response.json();
    }).then((response) => {
      const newData = [...fetchedData]
      newData[index] = response.unit_template
      setFetchedData(newData)
      alert('Activity Pack has been saved.')
    }).catch((error) => {
      // to do, use Sentry to capture error
    })
  }

  function renderActivitiesTable() {
    if(loadingTableData && fetchedData.length === 0) { return }

    const filteredUnitTemplates = orderedUnitTemplates({ diagnostic, fetchedData, flag, packType, searchByActivityPack, searchInput });
    const unitTemplateRows = filteredUnitTemplates.map((unitTemplate) => (
      <UnitTemplateRow
        data-sortable-label={unitTemplate.name}
        handleDelete={onDelete}
        key={unitTemplate.id}
        unitTemplate={unitTemplate}
        updateUnitTemplate={updateUnitTemplate}
      />
    ));
    const packCountLabel = filteredUnitTemplates.length === 1 ? 'activity pack' : 'activity packs'
    return(
      <div className="activity-packs-table">
        <p className="activity-pack-count">{`${filteredUnitTemplates.length} ${packCountLabel}`}</p>
        <table>
          {renderTableHeader()}
          <SortableList
            accessibilityContainer={document.body}
            data={unitTemplateRows}
            overlayInPortal={true}
            renderOverlayItem={renderDragPreview}
            showDropIndicator={true}
            sortCallback={updateOrder}
            useDragHandle={true}
          />
        </table>
        {!filteredUnitTemplates.length && <p className="no-matching-activity-packs">{NO_RESULTS_MESSAGE}</p>}
      </div>
    )
  }

  function renderTableHeader() {
    return (
      <thead>
        <tr className="unit-template-headers">
          <th className="controls-col"><span className="sr-only">Reorder and expand</span></th>
          <th className="name-col">Name</th>
          <th className="flag-col">Flag</th>
          <th className="diagnostics-col">Diagnostics</th>
          <th className="category-col">
            <Tooltip
              tooltipText="Unit Template Category"
              tooltipTriggerText="Pack Type"
            />
          </th>
          <th className="action-col"><span className="sr-only">Preview</span></th>
          <th className="action-col"><span className="sr-only">Edit</span></th>
          <th className="action-col"><span className="sr-only">Delete</span></th>
        </tr>
      </thead>
    )
  }

  function switchDiagnostic(diagnostic) {
    setDiagnostic(diagnostic)
  }

  function handleSearch(e) {
    const { target } = e;
    const { value } = target;
    setSearchInput(value)
  }

  function switchFlag(flag) {
    setFlag(flag)
  }

  function switchPackType(packType) {
    setPackType(packType)
  }

  function handleRadioChange() {
    setSearchByActivityPack(!searchByActivityPack);
    setSearchInput('');
  }

  return (
    <div className="cms-unit-templates index-container">
      <div className="unit-template-inputs">
        <UnitTemplateFilterInputs
          diagnostic={diagnostic}
          diagnostics={diagnostics}
          flag={flag}
          handleRadioChange={handleRadioChange}
          handleSearch={handleSearch}
          options={flagOptionValues}
          packType={packType}
          searchByActivityPack={searchByActivityPack}
          searchInput={searchInput}
          switchDiagnostic={switchDiagnostic}
          switchFlag={switchFlag}
          switchPackType={switchPackType}
        />
      </div>
      {renderActivitiesTable()}
      {loadingTableData && <Spinner />}
      {error && <p className="error-message">{error}</p>}
    </div>
  )
}

export default UnitTemplates
